import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import * as UITooltip from '@/components/ui/tooltip'
import { MessageSquare, BarChart3, Clock, Activity } from 'lucide-react'
import {
  formatNumber,
  formatDateRangeForDisplay,
  safeNumber,
} from '@/utils/reporting-formatters'
import { SingleChart } from './ChartComponents'
import { buildChartData } from '@/utils/build-chart-data'
import type {
  SmsReportResponse,
  ActivityReportResponse,
} from '@/lib/api/reporting.client'
import { Heading, Text } from '@/components/ui/typography'
import { MetricCard } from '@/components/ui/metric-card'

interface SmsTabProps {
  smsReport: SmsReportResponse | null
  activityReport: ActivityReportResponse | null
  dateRange: string
  appliedCustomDates: { start: string; end: string } | null
  chartType: 'line' | 'bar'
  setChartType: (type: 'line' | 'bar') => void
  isLoading: boolean
}

export function SmsTab({
  smsReport,
  activityReport,
  dateRange,
  appliedCustomDates,
  chartType,
  setChartType,
  isLoading,
}: SmsTabProps) {
  const dateRangeText = formatDateRangeForDisplay(dateRange, appliedCustomDates)
  const chartData = buildChartData(activityReport, dateRange)
  const hasSmsData =
    activityReport?.dataPoints?.some(
      point => point.smsHandled > 0 || point.smsResolved > 0,
    ) ||
    (smsReport &&
      (safeNumber(smsReport.handledByAI) > 0 ||
        safeNumber(smsReport.resolvedSMS) > 0))

  return (
    <div className="space-y-6 mt-6 animate-in fade-in duration-300">
      {/* Summary Stats Row - All Cards Above Chart */}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {isLoading ? (
          [1, 2, 3, 4].map(i => (
            <Card key={i} className="border border-gray-200">
              <CardContent className="p-3 relative">
                <Skeleton className="absolute -top-2 -left-2 h-8 w-8 rounded-lg" />
                <Skeleton className="h-4 w-24 mx-auto mb-3 mt-2" />
                <Skeleton className="h-10 w-16 mx-auto mb-3" />
                <Skeleton className="h-4 w-full" />
              </CardContent>
            </Card>
          ))
        ) : smsReport ? (
          <>
            <MetricCard
              icon={MessageSquare}
              title="Message sessions"
              metric={formatNumber(smsReport.handledByAI)}
              description="Total sessions answered by AI"
              info={`Message sessions measures the total number of SMS conversations initiated and handled by AI. ${formatNumber(smsReport.handledByAI)} sessions were handled in ${dateRangeText}.`}
            />
            <MetricCard
              icon={Clock}
              title="Open sessions"
              metric={formatNumber(smsReport.openSessionsCount ?? 0)}
              description="Currently active conversations"
              info={`Open sessions measures the number of active conversations that are still ongoing and have not been closed or transferred to a human agent. ${formatNumber(smsReport.openSessionsCount ?? 0)} sessions are currently open.`}
            />
            <MetricCard
              icon={MessageSquare}
              title="Messages sent"
              metric={formatNumber(smsReport.totalMessagesSent ?? 0)}
              description="Total across all sessions"
              info={`Messages sent measures the combined count of all SMS messages sent by AI across all conversations. ${formatNumber(smsReport.totalMessagesSent ?? 0)} messages were sent in ${dateRangeText}.`}
            />
            <MetricCard
              icon={BarChart3}
              title="Avg turns per session"
              metric={formatNumber(smsReport.averageTurnsPerSession ?? 0)}
              description="Average message exchanges"
              info={`Average turns per session measures the typical number of back-and-forth message exchanges per conversation, calculated by dividing total messages by total sessions. The average session had ${formatNumber(smsReport.averageTurnsPerSession ?? 0)} turns in ${dateRangeText}.`}
            />
          </>
        ) : (
          <Card className="col-span-full text-center border border-gray-200 rounded-lg p-4">
            <div className="py-12">
              <Text variant="muted">
                No SMS data available for the selected date range.
              </Text>
            </div>
          </Card>
        )}
      </div>

      {/* Activity Chart */}
      <div className="space-y-6">
        {/* Chart Type Selector */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
          <div>
            <Heading variant="section-title" as="h2">
              Daily messages activity
            </Heading>
            <Text variant="muted" className="mt-1">
              Messages handled over time
            </Text>
          </div>
          {!isLoading && (
            <UITooltip.TooltipProvider>
              <div className="flex items-center gap-2 bg-gray-100 dark:bg-muted p-1 rounded-lg w-fit">
                <UITooltip.Tooltip>
                  <UITooltip.TooltipTrigger asChild>
                    <Button
                      variant={chartType === 'line' ? 'default' : 'ghost'}
                      size="sm"
                      className="h-8 px-3"
                      onClick={() => setChartType('line')}
                    >
                      <Activity className="h-4 w-4" />
                    </Button>
                  </UITooltip.TooltipTrigger>
                  <UITooltip.TooltipContent>
                    <p>Line Chart - Shows trends over time</p>
                  </UITooltip.TooltipContent>
                </UITooltip.Tooltip>
                <UITooltip.Tooltip>
                  <UITooltip.TooltipTrigger asChild>
                    <Button
                      variant={chartType === 'bar' ? 'default' : 'ghost'}
                      size="sm"
                      className="h-8 px-3"
                      onClick={() => setChartType('bar')}
                    >
                      <BarChart3 className="h-4 w-4" />
                    </Button>
                  </UITooltip.TooltipTrigger>
                  <UITooltip.TooltipContent>
                    <p>Bar Chart - Compares values across periods</p>
                  </UITooltip.TooltipContent>
                </UITooltip.Tooltip>
              </div>
            </UITooltip.TooltipProvider>
          )}
        </div>

        <SingleChart
          title=""
          description=""
          dataKey1="smsHandledByAI"
          color1="#8b7aa8"
          name1="Sessions"
          icon={MessageSquare}
          hasData={hasSmsData ?? false}
          chartType={chartType}
          chartData={chartData}
          isLoading={isLoading}
        />
      </div>
    </div>
  )
}
